import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    House, Package, Car, ShoppingCart, Envelope, Gear, SignOut, List, X,
    ChartBar, Sun, Moon
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const LOGO_GLOBAL = "https://customer-assets.emergentagent.com/job_mmd-global/artifacts/84lvvojb_WhatsApp%20Image%202026-03-25%20at%2021.12.59.jpeg";

const navItems = [
    { path: '/administration/dashboard', icon: ChartBar, label: 'Dashboard' },
    { path: '/administration/products', icon: Package, label: 'Produits' },
    { path: '/administration/cars', icon: Car, label: 'Voitures' },
    { path: '/administration/orders', icon: ShoppingCart, label: 'Commandes' },
    { path: '/administration/messages', icon: Envelope, label: 'Messages' },
    { path: '/administration/settings', icon: Gear, label: 'Paramètres' },
];

export default function AdminLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/administration');
    };

    return (
        <div data-testid="admin-layout" className={`min-h-screen flex ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`}>
            {/* Sidebar */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-64 ${theme === 'dark' ? 'bg-mmd-surface-dark border-white/10' : 'bg-white border-black/10'} border-r transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className={`p-6 border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
                        <Link to="/" className="flex items-center gap-3">
                            <img src={LOGO_GLOBAL} alt="MMD Global" className="h-10 w-10 rounded-full" />
                            <div>
                                <p className={`font-heading font-semibold ${theme === 'dark' ? 'text-white' : 'text-mmd-navy'}`}>MMD Global</p>
                                <p className={`text-xs ${theme === 'dark' ? 'text-mmd-silver/60' : 'text-mmd-navy/60'}`}>Administration</p>
                            </div>
                        </Link>
                    </div>

                    {/* Nav */}
                    <nav className="flex-1 p-4 space-y-1">
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                                    location.pathname === item.path
                                        ? theme === 'dark' ? 'bg-mmd-gold/20 text-mmd-gold' : 'bg-mmd-navy/10 text-mmd-navy'
                                        : theme === 'dark' ? 'text-mmd-silver/70 hover:text-white hover:bg-white/5' : 'text-mmd-navy/70 hover:text-mmd-navy hover:bg-black/5'
                                }`}
                            >
                                <item.icon size={20} />
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    {/* User & Logout */}
                    <div className={`p-4 border-t ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            data-testid="admin-theme-toggle"
                            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium mb-3 transition-all ${
                                theme === 'dark' 
                                    ? 'bg-mmd-gold/20 text-mmd-gold hover:bg-mmd-gold/30' 
                                    : 'bg-mmd-navy/10 text-mmd-navy hover:bg-mmd-navy/20'
                            }`}
                        >
                            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                            {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
                        </button>
                        
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-mmd-gold/20' : 'bg-mmd-navy/10'}`}>
                                <span className={`font-medium ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>{user?.username?.[0]?.toUpperCase() || 'A'}</span>
                            </div>
                            <div>
                                <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-mmd-navy'}`}>{user?.username || 'Admin'}</p>
                                <p className={`text-xs ${theme === 'dark' ? 'text-mmd-silver/60' : 'text-mmd-navy/60'}`}>{user?.role || 'Administrateur'}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            data-testid="admin-logout"
                            className="flex items-center gap-2 w-full px-4 py-2 rounded-lg text-sm text-red-500 hover:bg-red-500/10 transition-colors"
                        >
                            <SignOut size={18} />
                            Déconnexion
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 lg:ml-64">
                {/* Top Bar */}
                <header className={`sticky top-0 z-40 backdrop-blur-xl border-b px-4 lg:px-8 py-4 ${theme === 'dark' ? 'bg-mmd-surface-dark/80 border-white/10' : 'bg-white/80 border-black/10'}`}>
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className={`lg:hidden p-2 rounded-lg ${theme === 'dark' ? 'text-white hover:bg-white/10' : 'text-mmd-navy hover:bg-black/10'}`}
                            data-testid="admin-menu-toggle"
                        >
                            {sidebarOpen ? <X size={24} /> : <List size={24} />}
                        </button>
                        <Link to="/" className={`text-sm transition-colors flex items-center gap-2 ${theme === 'dark' ? 'text-mmd-silver/70 hover:text-mmd-gold' : 'text-mmd-navy/70 hover:text-mmd-navy'}`}>
                            <House size={16} />
                            Voir le site
                        </Link>
                    </div>
                </header>

                {/* Page Content */}
                <main className="p-4 lg:p-8">
                    <Outlet />
                </main>
            </div>

            {/* Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}
        </div>
    );
}
